/**
 * Dependencies
 */
import _ from 'lodash';
import React, { useEffect, useCallback } from 'react';
import { Location } from 'history';

import LoadingN from '@nerdwallet/react-loading-n';
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Box } from '@nerdwallet/currency';
import { MarketplaceComparisonTableContextProvider } from '@nerdwallet/structured-content/contexts/MarketplaceComparisonTableContext';

import { getUrlPath } from '../../lib/article-utils';

import { NotFound, RedirectWithStatusCode } from './status-code';
import withArticleQuery from './article-query';
import { ArticleFragmentFragment } from '~/generated-gql/generated-types';
import { ReactRouterChildrenProps } from '~/lib/types';
import { MemoizedArticleFragment } from './article-fragment';

/**
 * Check whether the current article path matches the current RR
 * location object.
 *
 * Note: this is used to verify whether or not we should redirect from
 * our old URL structure to our new.
 *
 * @param {Object} article The query0 article.
 * @param {Object} locationObject The RR location object.
 *
 * @return {bool} Whether or not the article and location match.
 */
export const articlePathMatchesLocation = (
  article: ArticleFragmentFragment,
  locationObject: Location
): boolean => {
  const articlePath = getUrlPath(article);
  const currentPath = _.get(locationObject, 'pathname');
  return articlePath === currentPath;
};

const loadingSpinner = (
  <Box display="flex" justifyContent="center" marginY="5">
    <LoadingN />
  </Box>
);

const handlePageChange = () => {
  if (window) {
    // Ensure we scroll to the top of the page for any page transitions.
    window.scrollTo(0, 0);
  }
};

type Props = ReactRouterChildrenProps & {
  data: any;
};

export const BaseArticleRoute: React.FC<Props> = ({
  data = {},
  match,
  location,
}) => {
  useEffect(() => {
    handlePageChange();
  }, [match?.params?.slug]);

  const getArticle = useCallback(() => {
    return data?.article ?? data?.articleFork;
  }, [data]);

  const renderArticle = () => {
    const article = getArticle();
    const slug = match?.params?.slug;

    if (
      // article is done loading. Assume we have the article we most recently fetched.
      // Any 404 or error logic should be handled earlier
      !data?.loading &&
      // and path doesn't match article link doesn't match
      !articlePathMatchesLocation(article, location) &&
      // and published article
      article.isPublished &&
      // and not fork draft
      !data?.articleFork &&
      // we have a path to redirect to
      getUrlPath(article)
    ) {
      const redirectPath = getUrlPath(article);
      // Fallback to '' to ensure we don't output 'null'
      const redirectParams = location?.search ?? '';
      return (
        <RedirectWithStatusCode
          to={`${redirectPath}${redirectParams}`}
          code={301}
        />
      );
    }
    const children = (
      <MemoizedArticleFragment
        article={article}
        slug={slug}
        loading={data?.loading}
      />
    );
    if (article?.id && article?.isPublished) {
      return (
        <Impression
          key={article.id}
          eventType="content_impression"
          payload={{
            content_id: Number(article.id),
            content_source: 'wp',
            content_id_type: 'post',
          }}
        >
          <MarketplaceComparisonTableContextProvider
            attributionTopic={article?.attributionTopic}
          >
            {children}
          </MarketplaceComparisonTableContextProvider>
        </Impression>
      );
    }
    return children;
  };

  const { loading } = data;
  const graphqlArticle = getArticle();

  const shouldBeLoading = loading && !graphqlArticle;
  const gqlError = data?.error?.message ?? '';

  // non-404 error. Throw the error and return 500
  if (
    !loading &&
    (!graphqlArticle || graphqlArticle.slug !== match?.params?.slug) &&
    gqlError &&
    gqlError.indexOf('404: Not Found') === -1
  ) {
    throw new Error(`Apollo error in ArticleRoute: ${gqlError}`);
  }

  if (!loading && !graphqlArticle) {
    return <NotFound />;

    // TODO-gql re-enable when the apollo state bug is solved
    // https://nerdwallet.atlassian.net/browse/FEI-1203
    // if (gqlError.indexOf('404: Not Found') !== -1) {
    //   return <NotFound />;
    // }
    // if (gqlError.indexOf('401: Unauthorized') !== -1) {
    //   return <Unauthorized />;
    // }
    // console.error(
    //   `Error during apollo query for ArticleRoute: ${data?.error?.message}`
    // );
    // return <StatusCode code={500} />;
  }

  return (
    <Box className="article-route-container">
      {/* Hack to deal with center-align bug: isolate loading spinner and article content.
      For more context, see https://nerdwallet.atlassian.net/browse/WPRM-209 */}
      <Box>
        {shouldBeLoading ? loadingSpinner : null}
        {!shouldBeLoading ? renderArticle() : null}
      </Box>
    </Box>
  );
};

BaseArticleRoute.displayName = 'ArticleRoute';

export default withArticleQuery(BaseArticleRoute);
